import { useState } from 'react'
import { Activity } from 'lucide-react'
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { useActivityTimeline } from '@/services/admin/metrics.service'
import { formatBrDate } from '@/components/super-admin/badges/format'
import { CHART_COLORS } from './types'
import type { DashboardFilters } from './types'

interface Props {
  filters: DashboardFilters
}

type GroupBy = 'day' | 'week' | 'month'

/**
 * Série temporal com 4 séries empilhadas (enrollments + episodes +
 * quizzes + badges). Toggle pra group by day/week/month.
 */
export function ActivityTimelineChart({ filters }: Props) {
  const [groupBy, setGroupBy] = useState<GroupBy>('day')

  const { data, isLoading } = useActivityTimeline({
    groupBy,
    dateFrom: filters.dateFrom?.toISOString(),
    dateTo: filters.dateTo?.toISOString(),
    companyId: filters.companyIds,
  })

  const chartData = (data?.buckets ?? []).map((b) => ({
    ...b,
    dateLabel: formatBrDate(b.date),
  }))

  return (
    <Card className="glass-card border-border/50">
      <CardHeader>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <Activity className="w-4 h-4 text-primary" />
              Atividade ao longo do tempo
            </CardTitle>
            <CardDescription>
              Novos enrollments · episódios completos · quizzes · badges
            </CardDescription>
          </div>
          <ToggleGroup
            type="single"
            size="sm"
            value={groupBy}
            onValueChange={(v) => v && setGroupBy(v as GroupBy)}
          >
            <ToggleGroupItem value="day" className="text-xs h-7 px-3">
              Dia
            </ToggleGroupItem>
            <ToggleGroupItem value="week" className="text-xs h-7 px-3">
              Semana
            </ToggleGroupItem>
            <ToggleGroupItem value="month" className="text-xs h-7 px-3">
              Mês
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-80 w-full" />
        ) : chartData.length === 0 ? (
          <p className="py-12 text-center text-sm text-muted-foreground">
            Sem atividade no período selecionado.
          </p>
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <AreaChart data={chartData} margin={{ top: 8, right: 12, left: -16, bottom: 0 }}>
              <defs>
                <linearGradient id="gradEnrollments" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={CHART_COLORS.sky} stopOpacity={0.6} />
                  <stop offset="95%" stopColor={CHART_COLORS.sky} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gradEpisodes" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={CHART_COLORS.emerald} stopOpacity={0.6} />
                  <stop offset="95%" stopColor={CHART_COLORS.emerald} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gradQuizzes" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={CHART_COLORS.fuchsia} stopOpacity={0.6} />
                  <stop offset="95%" stopColor={CHART_COLORS.fuchsia} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gradBadges" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={CHART_COLORS.primary} stopOpacity={0.6} />
                  <stop offset="95%" stopColor={CHART_COLORS.primary} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272A" vertical={false} />
              <XAxis
                dataKey="dateLabel"
                stroke="#71717A"
                fontSize={11}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                stroke="#71717A"
                fontSize={11}
                tickLine={false}
                axisLine={false}
                allowDecimals={false}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'hsl(220 20% 9%)',
                  border: '1px solid hsl(220 15% 18%)',
                  borderRadius: 8,
                  fontSize: 12,
                }}
                labelStyle={{ color: '#fff', fontWeight: 600, marginBottom: 4 }}
              />
              <Legend
                wrapperStyle={{ fontSize: 11, paddingTop: 12 }}
                iconType="circle"
                iconSize={8}
              />
              <Area
                type="monotone"
                dataKey="enrollments"
                name="Matrículas"
                stroke={CHART_COLORS.sky}
                fill="url(#gradEnrollments)"
                strokeWidth={2}
              />
              <Area
                type="monotone"
                dataKey="episodesCompleted"
                name="Episódios"
                stroke={CHART_COLORS.emerald}
                fill="url(#gradEpisodes)"
                strokeWidth={2}
              />
              <Area
                type="monotone"
                dataKey="quizzesCompleted"
                name="Quizzes"
                stroke={CHART_COLORS.fuchsia}
                fill="url(#gradQuizzes)"
                strokeWidth={2}
              />
              <Area
                type="monotone"
                dataKey="badgesGranted"
                name="Badges"
                stroke={CHART_COLORS.primary}
                fill="url(#gradBadges)"
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  )
}
